import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { LocationEntity } from 'src/app/core/services/location/domain/entities/location.entity';
import { ProvinceEntity } from 'src/app/core/services/location/domain/entities/province.entity';
import { GetDepartmentsUseCase } from 'src/app/core/services/location/domain/usecases/get-departments.usecase';
import { GetLocationsUseCase } from 'src/app/core/services/location/domain/usecases/get-locations.usecase';
import { GetProvinceUseCase } from 'src/app/core/services/location/domain/usecases/get-province.usecase';
import { Failure } from 'src/app/core/utils/failure';
import { GetFileModel } from 'src/app/features/claims/data/models/get-file.model';
import { LocalitiesEntity } from 'src/app/features/claims/data/models/location.model';
import { ClaimMultiEntity } from 'src/app/features/claims/domain/entities/claim-multi.entity';
import { GetFileMultiUseCase } from 'src/app/features/claims/domain/usecases/get-file-multi.usecase';
import { GetCompanyCuilUseCase } from 'src/app/features/company/domain/usecases/get-company-colective.usecase';
import { UserData } from 'src/app/features/user/domain/entities/user-data.entity';
import { Departament } from 'src/app/features/user/presentation/components/New-User-SuperAdmin/newUser.component';

interface Base64 {
  name: string;
  base64: string;
}

@Component({
  selector: 'app-personal-details-multi',
  templateUrl: './personal-details-multi.component.html',
  styleUrls: ['./personal-details-multi.component.css'],
})
export class PersonalDetailsMultiComponent implements OnInit {
  @Output() stepIndex: EventEmitter<number> = new EventEmitter();
  @Output() claimantDetailsForm = new EventEmitter<FormGroup>();
  @Output() activeModal: EventEmitter<boolean> = new EventEmitter();
  @Input() claim?: ClaimMultiEntity;
  @Input() userData?: UserData;

  isDragging: boolean = false;
  maxDate?: Date;
  minDate: Date = new Date(1930, 0, 1);

  pdfFiles: any[] = [];
  pdfFilesBase64: string[] = [];

  departments: Departament[] = [];
  provinces: ProvinceEntity[] = [];
  localities: LocalitiesEntity[] = [];

  formGroup: FormGroup = this.formBuilder.group({
    representative_last_name: ['', Validators.required],
    representative_name: ['', Validators.required],
    representative_cuil: ['', Validators.required],
    representative_email: ['', Validators.required],
    representative_phone: [''],
    representative_street: ['', Validators.required],
    representative_number: ['', Validators.required],
    representative_floor_apartment: [''],
    representative_tower: [''],
    representative_postal_code: ['', [Validators.required]],
    representative_province: ['', [Validators.required]],
    representative_city: ['', [Validators.required]],
    departmentId: ['', Validators.required],
    claimantTypePerformance: ['', Validators.required],
    file: [[]],
  });

  constructor(
    private messageService: MessageService,
    private getProvince: GetProvinceUseCase,
    private getDepartmentsUsecase: GetDepartmentsUseCase,
    private getLocations: GetLocationsUseCase,
    private formBuilder: FormBuilder,
    private getFileUseCase: GetFileMultiUseCase
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadProvinces();
    await this.loadDepartments();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    await this.loadProvinces();
    if (changes['claim'] && changes['claim'].currentValue) {
      await this.initializeFormWithClaimData();
    }
    if (changes['userData'] && changes['userData'].currentValue) {
      this.loadUserData();
    }
  }

  async initializeFormWithClaimData() {
    if (this.claim) {
      this.formGroup.patchValue({
        representative_name: this.claim?.representative_name,
        representative_last_name: this.claim?.representative_last_name,
        representative_cuil: this.claim?.representative_cuil,
        representative_email: this.claim?.representative_email,
        representative_phone: this.claim?.representative_phone,
        representative_street: this.claim?.representative_email,
        representative_number: this.claim?.representative_number,
        representative_floor_apartment:
          this.claim?.representative_floor_apartment,
        representative_tower: this.claim?.representative_tower,
        representative_postal_code: this.claim?.representative_postal_code,
        representative_province: this.claim?.representative_province,
        representative_city: this.claim?.representative_city,
        claimantTypePerformance: this.claim?.claimantTypePerformance,
        departmentId: this.claim?.departmentId,
      });
      await this.initializeFormWithSavedFiles();
    }
  }

  private async initializeFormWithSavedFiles(): Promise<void> {
    try {
      if (this.claim?.file?.length) {
        try {
          this.pdfFiles = [];

          let i = 0;
          for (const fileName of this.claim.file) {
            const paycheckFile = new GetFileModel(this.claim.id!, fileName);
            const base64 = await this.getFile(paycheckFile);

            if (base64) {
              let base64Objet: Base64 = {
                name: 'Documento ' + (i + 1),
                base64: 'data:application/pdf;base64,' + base64,
              };

              this.pdfFiles.push(base64Objet);
              i++;
            } else {
              console.error(`Archivo ${fileName} vacío o inválido.`);
            }
          }

          if (this.pdfFiles.length === 0) {
            console.warn('Ningún archivo válido fue procesado.');
          }
        } catch (error) {
          console.error(
            'Error al obtener archivos de recibo de sueldo:',
            error
          );
          this.pdfFiles = [];
        }
      } else {
        console.warn(
          'No se encontraron archivos de recibo de sueldo para procesar.'
        );
      }
      let arrayBase64 = this.pdfFiles.map((file) => file.base64);

      this.formGroup.get('file')?.setValue(arrayBase64);
    } catch (error) {
      console.error(
        'Error general al inicializar archivos en el formulario:',
        error
      );
    }
  }

  loadUserData() {
    if (this.userData) {
      this.formGroup.patchValue({
        representative_name: this.userData.name,
        representative_last_name: this.userData.lastName,
        representative_cuil: this.userData.cuil,
        representative_email: this.userData.email,
        representative_phone: this.userData.phone,
        representative_street: this.userData.street,
        representative_number: this.userData.number,
        representative_floor_apartment: this.userData.floorApartment,
        representative_tower: this.userData.tower,
        representative_postal_code: this.userData.postalCode,
        representative_province: this.getProvinceIdByName(
          this.userData.province
        ),
        representative_city: this.userData.city,
        claimantTypePerformance: this.userData.type_responsibility,
      });
    }
  }

  async getFile(param: GetFileModel): Promise<string> {
    let result = await this.getFileUseCase.execute(param);
    if (result instanceof Failure) {
      return result.message;
    }
    return result;
  }

  getProvinceIdByName(provinceName: string | undefined) {
    const province = this.provinces.find((p) => p.id === provinceName);
    return province ? province.name : undefined;
  }

  async loadProvinces() {
    try {
      const result = await this.getProvince.execute();
      if (Array.isArray(result)) {
        this.provinces = result;
      }
    } catch (error) {
      console.error('Error fetching provinces', error);
    }
  }

  async loadLocalities(provinceId: string) {
    try {
      const localities = await this.getLocations.execute(provinceId);
      if (Array.isArray(localities)) {
        const localitiesList = this.putIdToLocalitiesList(localities);

        this.localities = localitiesList;
      }
    } catch (error) {
      console.error('Error fetching localities', error);
    }
  }

  putIdToLocalitiesList(list: LocationEntity[]): LocalitiesEntity[] {
    let listWithId: LocalitiesEntity[] = [];
    for (let i = 0; i < list.length; i++) {
      let city: LocalitiesEntity = {
        id: i,
        name: list[i].name,
      };
      listWithId.push(city);
    }
    return listWithId;
  }

  async loadDepartments() {
    const departments = await this.getDepartmentsUsecase.execute();
    if (Array.isArray(departments)) {
      const transformedDepartments = this.putIdToDepartmentsList(departments);
      this.departments = [
        { id: 'Otro', name: 'Otro' },
        ...transformedDepartments,
      ];
    }
  }

  putIdToDepartmentsList(list: Departament[]): Departament[] {
    let listWithId: Departament[] = [];
    for (let i = 0; i < list.length; i++) {
      let department: Departament = {
        id: list[i].id,
        name: list[i].name,
      };
      listWithId.push(department);
    }
    return listWithId;
  }

  nextStep() {
    this.claimantDetailsForm.emit(this.formGroup.value);
    this.stepIndex.emit(1);
  }

  cancel() {
    this.claimantDetailsForm.emit(this.formGroup);
    this.activeModal.emit(true);
  }

  onDragOver(event: Event) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDropSuccess(event: any, file: string): any {
    const files = event.dataTransfer?.files;

    if (!files || files.length === 0) return;

    event.preventDefault();
    this.isDragging = false;
    this.onFileChange(files);
  }

  private onFileChange(files: any) {
    const newFiles = Array.from(files);
    this.pdfFiles = (this.pdfFiles || []).concat(newFiles);
  }

  onChange(event: any, file: string): any {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(event.target.files) as File[];
    const fileForm = file;

    if (selectedFiles.length > 0) {
      if (this.pdfFiles.length >= 12) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Solo puedes subir hasta 12 archivos (JPG, WEBP, HEIF o PDF).',
        });
        return false;
      }

      const validTypes = [
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/heif',
        'application/pdf',
      ];

      const validFiles = selectedFiles.filter((file) => {
        if (file.size > 20 * 1024 * 1024) {
          this.messageService.add({
            severity: 'error',
            summary: 'Archivo demasiado grande',
            detail: `El archivo "${file.name}" excede el límite de 20 MB.`,
          });
          return false;
        }

        if (!validTypes.includes(file.type)) {
          this.messageService.add({
            severity: 'error',
            summary: 'Tipo de archivo no permitido',
            detail: `El archivo "${file.name}" no es un formato válido.`,
          });
          return false;
        }

        return true;
      });

      validFiles.forEach((file) => {
        this.convertToBase64(file, fileForm);
      });

      input.value = '';
    }
  }

  private async convertToBase64(file: any, fileForm: string) {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result?.toString();
      if (base64String) {
        this.assignFilesReceipt(file, base64String);
      }
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
    };
    reader.readAsDataURL(file);
  }

  private assignFilesReceipt(file: any, base64String: string) {
    if (this.pdfFiles.length >= 12) return;

    this.pdfFiles.push({ name: file.name, base64: base64String });

    let filePaycheckBase64Array = this.pdfFiles.map((file) => file.base64);
    this.formGroup.get('file')?.setValue(filePaycheckBase64Array);
    this.formGroup.get('file')?.markAsTouched();
    this.formGroup.get('file')?.updateValueAndValidity();
  }

  deleteFilePfd(index: number) {
    this.pdfFiles.splice(index, 1);
    const fileReceiptBase64Array = this.pdfFiles.map((file) => file.base64);
    this.formGroup
      .get('file')
      ?.setValue(fileReceiptBase64Array.length ? fileReceiptBase64Array : null);
    this.formGroup.get('file')?.markAsTouched();
    this.formGroup.get('file')?.markAsDirty();
  }

  hasErrors(controlName: string, errorType: string) {
    return (
      this.formGroup.get(controlName)?.hasError(errorType) &&
      this.formGroup.get(controlName)?.touched
    );
  }

  parseDateBirth(date: any): Date | null {
    if (!date) return null;
    return new Date(date);
  }
}
